/**
 * pdf.js worker-teardown errors — the single source of truth for these strings.
 *
 * Lives in `lib/` rather than beside the viewer because it has two consumers in
 * different layers, and they used to keep hand-typed copies that drifted apart:
 *
 *   - `components/ui/pdfViewer.tsx` — `ErrorComponent` auto-resets the error
 *     boundary, and `onError` decides whether to report a crash to ops.
 *   - `lib/ops-log.ts` — folds these into `SAFE_MESSAGE_MARKERS`, since a fixed
 *     library string is by definition safe to forward.
 *
 * Add a new teardown marker here and both behaviours follow. That is the point:
 * `Worker was destroyed` was previously listed in `ops-log.ts` only, so pdf.js
 * teardown was reported as `DOCUMENT_VIEWER_CRASH` (Bugbot, !398).
 */

/**
 * Messages pdf.js produces when its worker is torn down with work still in
 * flight — page flip, remount, unmount. These are **self-healing**: the boundary
 * resets and the page redraws, so the user sees nothing.
 *
 * They must not be reported to ops. Reporting them would bury real viewer
 * crashes in recovered noise, and `onError` also remounts the viewer, so a
 * teardown that repeats would emit a datapoint each time (CloudWatch bills per
 * datapoint).
 *
 * This is a SUBSET of `SAFE_MESSAGE_MARKERS` in `lib/ops-log.ts`, not a copy of
 * it — that list answers a different question ("is this text safe to forward?"),
 * and it imports this one rather than restating it. The two are not
 * interchangeable: `Invalid PDF structure` is safe to forward *and* worth
 * reporting (a genuinely broken document), so it belongs only in that list.
 */
export const TRANSIENT_PDF_WORKER_MARKERS: readonly string[] = [
  'sendWithPromise',
  'messageHandler is null',
  "Cannot read properties of null (reading 'sendWithPromise')",
  // Thrown when `worker.destroy()` lands while a task is still pending — the
  // same unmount/page-flip race as the others, just a different pdf.js code
  // path. Present in ops-log's SAFE_MESSAGE_MARKERS, which is how it slipped
  // past this check and got reported as a crash.
  'Worker was destroyed',
];

/**
 * Whether a thrown value is the pdf.js worker-teardown race rather than a real
 * render failure.
 */
export const isTransientPdfWorkerError = (error: unknown): boolean => {
  const s = (error as { toString?: () => string })?.toString?.() ?? '';
  return TRANSIENT_PDF_WORKER_MARKERS.some((marker) => s.includes(marker));
};
